import React, { useState, useEffect, useRef, useCallback } from "react";
import { loadGoogleMaps, parseAddressComponents } from "utils/googleMaps";

const LocationMap = ({
  latitude,
  longitude,
  onLocationChange,
  height = "300px",
  zoom = 15,
}) => {
  const containerRef = useRef(null);
  const mapDivRef = useRef(null);
  const mapInstance = useRef(null);
  const markerInstance = useRef(null);
  const geocoderInstance = useRef(null);
  const onLocationChangeRef = useRef(onLocationChange);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [error, setError] = useState(null);
  onLocationChangeRef.current = onLocationChange;

  const addMarker = useCallback((lat, lng) => {
    if (!mapInstance.current || !window.google) return;

    if (markerInstance.current) {
      markerInstance.current.setMap(null);
      markerInstance.current = null;
    }

    const position = { lat, lng };
    markerInstance.current = new window.google.maps.Marker({
      position,
      map: mapInstance.current,
      draggable: true,
      animation: window.google.maps.Animation.DROP,
    });

    markerInstance.current.addListener("dragend", () => {
      const pos = markerInstance.current.getPosition();
      handleLocationSelect(pos.lat(), pos.lng());
    });

    mapInstance.current.setCenter(position);
  }, []);

  const handleLocationSelect = useCallback(
    (lat, lng) => {
      if (!geocoderInstance.current) return;

      geocoderInstance.current.geocode({ location: { lat, lng } }, (results, status) => {
        if (status !== "OK" || !results?.[0]) return;
        const result = results[0];
        const parsed = parseAddressComponents(result.address_components || []);
        const line1 =
          parsed.streetNumber && parsed.streetName
            ? `${parsed.streetNumber} ${parsed.streetName}`
            : result.formatted_address || "";

        onLocationChangeRef.current?.({
          latitude: lat,
          longitude: lng,
          formattedAddress: result.formatted_address,
          line1,
          city: parsed.city,
          state: parsed.state,
          stateName: parsed.stateName,
          postalCode: parsed.postalCode,
          country: parsed.country,
          countryName: parsed.countryName,
        });
        addMarker(lat, lng);
      });
    },
    [addMarker],
  );

  useEffect(() => {
    let isMounted = true;

    const initMap = async () => {
      try {
        const google = await loadGoogleMaps();
        if (!isMounted || !containerRef.current || mapInstance.current) return;
        if (!google.maps.Map) throw new Error("Google Maps Map class not loaded");

        geocoderInstance.current = new google.maps.Geocoder();

        const innerDiv = document.createElement("div");
        innerDiv.style.width = "100%";
        innerDiv.style.height = "100%";
        containerRef.current.appendChild(innerDiv);
        mapDivRef.current = innerDiv;

        const center = {
          lat: latitude || 40.7128,
          lng: longitude || -74.006,
        };

        mapInstance.current = new google.maps.Map(innerDiv, {
          center,
          zoom,
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: false,
          zoomControl: true,
          styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }],
        });

        mapInstance.current.addListener("click", (e) => {
          handleLocationSelect(e.latLng.lat(), e.latLng.lng());
        });

        if (latitude && longitude) addMarker(latitude, longitude);
        if (isMounted) setMapLoaded(true);
      } catch (err) {
        console.error("Map initialization error:", err);
        if (isMounted) setError("Failed to load Google Maps. Please check your API key or network connection.");
      }
    };

    initMap();

    return () => {
      isMounted = false;
      if (markerInstance.current) {
        markerInstance.current.setMap(null);
        markerInstance.current = null;
      }
      if (mapInstance.current && window.google) {
        window.google.maps.event.clearInstanceListeners(mapInstance.current);
      }
      mapInstance.current = null;
      geocoderInstance.current = null;
      if (mapDivRef.current && mapDivRef.current.parentNode) {
        mapDivRef.current.parentNode.removeChild(mapDivRef.current);
      }
      mapDivRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (mapInstance.current && latitude && longitude) {
      addMarker(latitude, longitude);
      mapInstance.current.setCenter({ lat: latitude, lng: longitude });
    }
  }, [latitude, longitude, addMarker]);

  return (
    <div
      style={{ height, width: "100%" }}
      className="rounded-lg border border-[#E9EDF9] overflow-hidden relative"
    >
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
      {!mapLoaded && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 border-4 border-[#4F46E5] border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-gray-500">Loading map...</span>
          </div>
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-50">
          <div className="text-center text-red-600 px-4">
            <p className="font-medium">Map Error</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationMap;